import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { FiMenu, FiX, FiPlay, FiLock, FiCheckCircle, FiChevronDown, FiChevronRight } from 'react-icons/fi';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const Sidebar = ({ isOpen, onClose, categoryId }) => {
  const [chapters, setChapters] = useState([]);
  const [expandedChapters, setExpandedChapters] = useState({});
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (categoryId) {
      fetchChapters();
    }
  }, [categoryId]);

  // Listen for chapter completion events
  useEffect(() => {
    const handleChapterCompleted = () => {
      if (categoryId) {
        fetchChapters();
      }
    };

    window.addEventListener('chapterCompleted', handleChapterCompleted);
    return () => {
      window.removeEventListener('chapterCompleted', handleChapterCompleted);
    };
  }, [categoryId]);

  useEffect(() => {
    // Auto-expand and highlight current chapter
    const pathParts = location.pathname.split('/');
    const chapterIdFromPath = pathParts[pathParts.length - 1];
    if (chapterIdFromPath && !isNaN(chapterIdFromPath)) {
      const currentChapter = chapters.find(c => c.id === parseInt(chapterIdFromPath));
      if (currentChapter) {
        setExpandedChapters({ [currentChapter.id]: true });
      }
    }
  }, [location.pathname, chapters]);

  const fetchChapters = async () => {
    try {
      const response = await axios.get(`${API_URL}/chapters?category_id=${categoryId}`);
      setChapters(response.data.chapters);
    } catch (error) {
      console.error('Erro ao buscar capítulos:', error);
    }
  };

  const handleChapterClick = (chapter) => {
    const isUnlocked = chapter.isUnlocked !== false;

    if (isUnlocked) {
      navigate(`/chapter/${chapter.id}`);
      if (window.innerWidth < 1024) {
        onClose();
      }
    }
  };

  const getChapterStatus = (chapter) => {
    const isActive = location.pathname.includes(`/chapter/${chapter.id}`);
    
    // Check if chapter is completed (completed === 1)
    if (chapter.completed === 1) {
      return { icon: FiCheckCircle, color: 'text-green-400', isActive };
    }
    
    const isUnlocked = chapter.isUnlocked !== false;
    
    if (isUnlocked) {
      return { icon: FiPlay, color: isActive ? 'text-blue-400' : 'text-blue-400', isActive };
    }
    
    return { icon: FiLock, color: 'text-gray-500', isActive: false };
  };

  if (!categoryId) {
    return null;
  }

  return (
    <>
      {/* Overlay for mobile */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <div
        className={`fixed lg:static inset-y-0 left-0 z-50 w-80 bg-gray-800 text-white transform transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-700">
          <div className="flex items-center gap-2">
            <FiMenu className="lg:hidden cursor-pointer" onClick={onClose} />
            <h2 className="text-lg font-semibold">Conteúdo</h2>
          </div>
          <button
            onClick={onClose}
            className="lg:hidden text-gray-400 hover:text-white"
          >
            <FiX className="text-xl" />
          </button>
        </div>

        {/* Chapters List */}
        <div className="overflow-y-auto h-[calc(100vh-64px)]">
          {chapters.map((chapter) => {
            const status = getChapterStatus(chapter);
            const StatusIcon = status.icon;
            const isUnlocked = chapter.isUnlocked !== false;
            const isActive = status.isActive;
            const isExpanded = expandedChapters[chapter.id];

            return (
              <div key={chapter.id} className="border-b border-gray-700">
                {/* Chapter Header */}
                <div
                  className={`flex items-center justify-between p-4 cursor-pointer hover:bg-gray-700 transition ${
                    isActive ? 'bg-gray-700 border-l-4 border-blue-400' : ''
                  } ${!isUnlocked ? 'opacity-60' : ''}`}
                  onClick={() => {
                    if (isUnlocked) {
                      handleChapterClick(chapter);
                    }
                  }}
                >
                  <div className="flex items-center gap-3 flex-1">
                    <StatusIcon
                      className={`${status.color} flex-shrink-0`}
                    />
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium truncate ${
                        isActive ? 'text-blue-400' : 'text-white'
                      }`}>
                        {chapter.title}
                      </p>
                      <p className="text-xs text-gray-400 mt-1">
                        Capítulo {chapter.order_index}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {chapter.completed === 1 && (
                      <FiCheckCircle className="text-green-400 text-sm" />
                    )}
                    {chapters.filter(c => c.id === chapter.id).length > 0 && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setExpandedChapters(prev => ({
                            ...prev,
                            [chapter.id]: !prev[chapter.id]
                          }));
                        }}
                        className="text-gray-400 hover:text-white"
                      >
                        {isExpanded ? <FiChevronDown /> : <FiChevronRight />}
                      </button>
                    )}
                  </div>
                </div>

                {/* Challenges List (if expanded) */}
                {isExpanded && (
                  <div className="bg-gray-900 pl-12 pr-4 pb-2">
                    {[1, 2].map((challengeNum) => (
                      <div
                        key={challengeNum}
                        className="py-2 text-sm text-gray-400 flex items-center gap-2"
                      >
                        <div className="w-1.5 h-1.5 rounded-full bg-gray-500"></div>
                        Desafio {challengeNum}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
};

export default Sidebar;
